export type BrandName = "ironshard" | "skyflok";

// Runtime brand configuration consumed by Vue components.
// Additional brand configuration lives in the .env chain, especially
// .env.brand.<brand> and .env.brand.<brand>.<mode>, for values needed during
// build-time env replacement or before Vue starts.
export type BrandConfig = {
  // Stable machine-readable brand id. Must match the BRAND env value.
  id: BrandName;
  // Human-readable brand name for short UI text and alt labels.
  displayName: string;
  // Browser title and router page-title suffix.
  consoleName: string;
  // Longer product name for places that need formal naming.
  fullName: string;
  // Terms and legal page linked from signup.
  legalUrl: string;
  // Whether unauthenticated users can create a new account without an invitation.
  selfSignupAllowed: boolean;
  // Short auth-screen product description shown next to the login/signup form.
  loginTagline: string;
  // Public asset paths relative to import.meta.env.BASE_URL.
  // Splash-screen logos are not listed here because index.html renders before
  // Vue loads this config. Define them in .env.brand.* via
  // VITE_APP_SPLASH_LOGO_LIGHT and VITE_APP_SPLASH_LOGO_DARK.
  assets: {
    // Logo shown on dark backgrounds.
    logoLight: string;
    // Logo shown on light backgrounds.
    logoDark: string;
    // Background image shown on the authentication layout.
    authBackground: string;
  };
  // Brand-specific generated image settings.
  identicon: {
    // Jdenticon hue degrees used for bucket identicons.
    hues: number[];
  };
};

const ironshard: BrandConfig = {
  id: "ironshard",
  displayName: "IronShard",
  consoleName: "IronShard Console",
  fullName: "IronShard Web Portal",
  legalUrl: "https://www.ironshard.ai/legal",
  selfSignupAllowed: true,
  loginTagline:
    "Give AI agents a secure place to read and write unstructured data, from documents and logs to datasets and generated artifacts. IronShard gives you full control over redundancy, storage locations, and S3-compatible access.",
  assets: {
    logoLight: "brands/ironshard/logos/logo-light.png",
    logoDark: "brands/ironshard/logos/logo-dark.png",
    authBackground: "media/auth/bg4.jpg",
  },
  identicon: {
    hues: [20, 25, 15],
  },
};

const skyflok: BrandConfig = {
  id: "skyflok",
  displayName: "SkyFlok",
  consoleName: "SkyFlok S3 Console",
  fullName: "SkyFlok Web Portal",
  legalUrl: "https://www.skyflok.com/legal",
  selfSignupAllowed: false,
  loginTagline:
    "SkyFlok Object Storage gives you full control over redundancy, storage locations, and S3-compatible access.",
  assets: {
    logoLight: "brands/skyflok/logos/skyflok-logo-aquamarine.png",
    logoDark: "brands/skyflok/logos/skyflok-logo-aquamarine.png",
    authBackground: "media/auth/bg5.jpg",
  },
  identicon: {
    hues: [180],
  },
};

const configs: Record<BrandName, BrandConfig> = {
  ironshard,
  skyflok,
};

const envBrand = import.meta.env.VITE_APP_BRAND as BrandName | undefined;

export const brandConfig = configs[envBrand || "ironshard"] || ironshard;
